import { useWorkshopMode } from "../../../../hooks/useWorkshopMode";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useWhiteLabelContext } from "../../../../WhiteLabelContext";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";
import { useProfileRepository } from "../../../../data/repositories/ProfileRepository";
import { DetailsForm } from "../../customer-create/components/organization-details-form.component";
import { EmailPreferenceForm } from "../subcomponents/email-preference-editor.component";
import { MyProfileViewModel } from "../my-profile.component";
import { OrderOptionsForm } from "../subcomponents/order-options-editor.component";
import { WhiteLabelConfigurationsForm } from "../subcomponents/white-label-editor.components";
import { Organization } from "../../../../core/models/entities/Organization";
import { Profile } from "../../../../core/models/entities/Profile";
import {
  Commission,
  CommissionProfile,
} from "../../../../core/models/entities/Commission";
import { VendorType } from "../../../../core/models/entities/enums/OrganizationType";
import {
  dissmissToast,
  useErrorToast,
  useLoadingToast,
  useSuccessToast,
} from "../../../components/Toast/Toast";
import { useLogoContext } from "../../design-editor2/contexts/logo-options.context";
import { toBase64 } from "../../../../utils/functions/ImageUtil";

type LogoPendingState =
  | { type: "none" }
  | { type: "upload"; file: File; previewUrl: string }
  | { type: "remove" };

interface Props {
  profile: Profile;
  organization: Organization;
}

export function useMyVendorProfile({
  profile,
  organization,
}: Props): MyProfileViewModel {
  const { organizationRepository, logoRepository } = useRepositoriesContext();
  const [profileConfigurations, setProfileConfigurations] = useState<Profile>({
    ...profile,
  });
  const [loading, setLoading] = useState<boolean>(true);
  const profileRepository = useProfileRepository();
  const { refetch: refetchProfile } = profileRepository.useProfile();
  const { t } = useTranslation();
  const { reset: resetWhiteLabel, updateWhiteLabelContext } =
    useWhiteLabelContext();
  const [transition, setTransition] = useState(false);
  const { options: logoOptions } = useLogoContext();
  const [commissionProfile, setCommissionProfile] = useState<CommissionProfile>(
    mapCommissionProfile(organization)
  );
  const [pendingLogoState, setPendingLogoState] = useState<LogoPendingState>({
    type: "none",
  });

  const [isDirty, setIsDirty] = useState(false);

  const [detailsForm, setDetailsForm] = useState<DetailsForm>({
    postCode: organization?.postCode ?? "",
    address: organization?.address ?? "",
    city: organization?.city ?? "",
    country: organization?.country ?? "",
    contactEmail: organization?.contactEmail ?? "",
    contactPerson: organization?.contactPerson ?? "",
    contactPhone: organization?.contactPhone ?? "",
    cvr: organization?.cvr ?? "",
    email: organization?.email ?? "",
    name: organization?.name ?? "",
    phone: organization?.phone ?? "",
    defaultVatRate: organization?.defaultVatRate ?? 0,

    shippingName: profile.shippingPreferences?.shippingName ?? "",
    shippingAddress: profile.shippingPreferences?.shippingAddress ?? "",
    shippingPostCode: profile.shippingPreferences?.shippingPostCode ?? "",
    shippingCity: profile.shippingPreferences?.shippingCity ?? "",
    shippingCountry: profile.shippingPreferences?.shippingCountry ?? "",
    shippingContactPerson:
      profile.shippingPreferences?.shippingContactPerson ?? "",
    shippingContactPhone:
      profile.shippingPreferences?.shippingContactPhone ?? "",
    shippingContactEmail:
      profile.shippingPreferences?.shippingContactEmail ?? "",
  });

  const updateOrganizationData = () =>
    setDetailsForm({
      postCode: organization?.postCode ?? "",
      address: organization?.address ?? "",
      city: organization?.city ?? "",
      country: organization?.country ?? "",
      contactEmail: organization?.contactEmail ?? "",
      contactPerson: organization?.contactPerson ?? "",
      contactPhone: organization?.contactPhone ?? "",
      cvr: organization?.cvr ?? "",
      email: organization?.email ?? "",
      name: organization?.name ?? "",
      phone: organization?.phone ?? "",
      defaultVatRate: organization?.defaultVatRate ?? 0,

      shippingName:
        organization?.shippingName ??
        profile.shippingPreferences?.shippingName ??
        "",
      shippingAddress:
        organization?.shippingAddress ??
        profile.shippingPreferences?.shippingAddress ??
        "",
      shippingPostCode:
        organization?.shippingPostCode ??
        profile.shippingPreferences?.shippingPostCode ??
        "",
      shippingCity:
        organization?.shippingCity ??
        profile.shippingPreferences?.shippingCity ??
        "",
      shippingCountry:
        organization?.shippingCountry ??
        profile.shippingPreferences?.shippingCountry ??
        "",
      shippingContactPerson:
        organization?.shippingContactPerson ??
        profile.shippingPreferences?.shippingContactPerson ??
        "",
      shippingContactPhone:
        organization?.shippingContactPhone ??
        profile.shippingPreferences?.shippingContactPhone ??
        "",
      shippingContactEmail:
        organization?.shippingContactEmail ??
        profile.shippingPreferences?.shippingContactEmail ??
        "",
    });

  useEffect(() => {
    setProfileConfigurations({ ...profile });
    setCommissionProfile(mapCommissionProfile(organization));
    updateOrganizationData();
  }, [organization]);

  function mapCommissionProfile(organization: Organization): CommissionProfile {
    return (
      organization?.commission ??
      ({
        typeOnly: [] as Commission[],
        methods: [] as Commission[],
      } as CommissionProfile)
    );
  }

  useEffect(() => {
    updateWhiteLabelContext({
      ...profileConfigurations?.whiteLabelConfigurations!,
    });
  }, [profileConfigurations]);

  useEffect(() => {
    organizationRepository
      .getVendorById(profile!.userOrganizationInformations.organizationId)
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        toast.error(t("toasts.profile_load_failed"));
      });
  }, []);

  const workshopMode = useWorkshopMode();

  // ----- Commission & VAT prefetch -----
  // The section's data stays lazily gated (it only mounts on first open), but
  // we warm its exact query caches in the background AFTER first paint so the
  // section usually opens instantly: GetTemplatesSizes is a heavy NAV call
  // (p50 ~0.8s, p95 ~6s) that must never gate the screen's first paint.
  // Fail-silent — on a miss the section's own hooks fetch exactly as before
  // (the spinner becomes the fallback, not the norm). Workshop-flagged users
  // never see the section, so nothing is prefetched for them.
  const commissionPrefetchFired = useRef(false);
  function prefetchCommissionData() {
    if (workshopMode || !organization?.id) return;
    if (commissionPrefetchFired.current) return;
    commissionPrefetchFired.current = true;
    logoRepository.prefetchOptions();
    logoRepository.prefetchTransferOptions(organization.id);
  }

  useEffect(() => {
    if (workshopMode || !organization?.id) return;
    // Idle-ish delay: well past the screen's first paint, long before a
    // typical section click. Hovering the nav item prefetches earlier.
    const timer = setTimeout(prefetchCommissionData, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopMode, organization?.id]);

  const isMainBranch =
    profile.userOrganizationInformations.vendorType === VendorType.MainBranch;

  const canEditWhiteLabel = isMainBranch || organization?.editAllowed === true;

  let sections = {
    details: { show: true, canEdit: false },
    whiteLabelling: { show: true, canEdit: canEditWhiteLabel },
    emailPreferences: { show: true, canEdit: true },
    // Workshop-flagged users must not see commission rates or margins.
    commissions: { show: !workshopMode, canEdit: true },
  };

  const initialEmailPreferenceform: EmailPreferenceForm = {
    emailPreferenceNewDesign:
      profileConfigurations?.emailPreferences?.emailPreferenceNewDesign ??
      false,
    emailPreferenceNewLogos:
      profileConfigurations?.emailPreferences?.emailPreferenceNewLogos ?? false,
    emailPreferenceNewOrder:
      profileConfigurations?.emailPreferences?.emailPreferenceNewOrder ?? false,
    emailPreferencePendingOrder:
      profileConfigurations?.emailPreferences?.emailPreferencePendingOrder ??
      false,
    emailPreferenceOrder:
      profileConfigurations?.emailPreferences?.emailPreferenceOrder ?? false,
  };

  function updateLogo(profile: Profile) {
    setProfileConfigurations({
      ...profileConfigurations!,
      whiteLabelConfigurations: {
        ...profileConfigurations!.whiteLabelConfigurations,
        logoUrl: profile.whiteLabelConfigurations.logoUrl,
      },
    });
  }

  function updateOrganizationLogoHandle(file: File) {
    // Deferred upload - store file and preview, upload on save
    toBase64(file).then((previewUrl) => {
      setPendingLogoState({ type: "upload", file, previewUrl });
      setIsDirty(true);
    });
  }

  function removeLogoHandle() {
    setPendingLogoState({ type: "remove" });
    setIsDirty(true);
  }

  function cancelLogoRemovalHandle() {
    setPendingLogoState({ type: "none" });
  }

  function updateCommission(commission: CommissionProfile) {
    setCommissionProfile(commission);
    setIsDirty(true);
  }

  function updateWhiteLabel(form: WhiteLabelConfigurationsForm) {
    setIsDirty(true);
    setProfileConfigurations({
      ...profileConfigurations!,
      whiteLabelConfigurations: {
        ...profileConfigurations!.whiteLabelConfigurations,
        colorHex: form.colorHex,
        whiteLabelContactEmail: form.whiteLabelContactEmail,
        whiteLabelContactPhone: form.whiteLabelContactPhone,
        whiteLabelCopyrightName: form.whiteLabelCopyrightName,
        whiteLabelVendorName: form.whiteLabelVendorName,
        whiteLabelWebsiteLink: form.whiteLabelWebsiteLink,
      },
    });
  }

  function updateDetails(form: DetailsForm) {
    setIsDirty(true);
    setProfileConfigurations({
      ...profileConfigurations!,
      detailedConfigurations: {
        ...profileConfigurations!.detailedConfigurations,
        contactEmail: form.contactEmail,
        contactPerson: form.contactPerson,
        contactPhone: form.contactPhone,
        organizationAddress: form.address,
        organizationCountry: form.country,
        organizationEmail: form.email,
        organizationCVR: form.cvr,
        organizationPhone: form.phone,
      },
      userOrganizationInformations: {
        ...profileConfigurations!.userOrganizationInformations,
        organizationName: form.name,
      },
      shippingPreferences: {
        shippingAddress: form.shippingAddress ?? "",
        shippingCity: form.shippingCity ?? "",
        shippingContactEmail: form.shippingContactEmail ?? "",
        shippingContactPerson: form.shippingContactPerson ?? "",
        shippingContactPhone: form.shippingContactPhone ?? "",
        shippingCountry: form.shippingCountry ?? "",
        shippingName: form.shippingName ?? "",
        shippingPostCode: form.shippingPostCode ?? "",
      },
    });
    setDetailsForm({
      ...detailsForm,
      // Contact fields: edited via the Users & contact primary-contact card;
      // saveProfileHandle persists them from detailsForm (updateOrganization),
      // so dropping them here would silently discard the change.
      contactPerson: form.contactPerson ?? "",
      contactPhone: form.contactPhone ?? "",
      contactEmail: form.contactEmail ?? "",
      shippingAddress: form.shippingAddress ?? "",
      shippingCity: form.shippingCity ?? "",
      shippingContactEmail: form.shippingContactEmail ?? "",
      shippingContactPerson: form.shippingContactPerson ?? "",
      shippingContactPhone: form.shippingContactPhone ?? "",
      shippingCountry: form.shippingCountry ?? "",
      shippingName: form.shippingName ?? "",
      shippingPostCode: form.shippingPostCode ?? "",
      defaultVatRate: form.defaultVatRate,
    });
  }

  function updateOrderOptions(form: OrderOptionsForm) {
    setIsDirty(true);
    setProfileConfigurations({
      ...profileConfigurations!,
      orderOptions: {
        orderOptionsTermsOfDelivery: form.orderOptionsTermsOfDelivery,
        orderOptionsTermsOfStandardPackaging:
          form.orderOptionsTermsOfStandardPackaging,
        orderOptionsTermsOfSpecificPackaging:
          form.orderOptionsTermsOfSpecificPackaging,
        orderOptionsDeliveryCost: form.orderOptionsDeliveryCost ?? 0,
        orderOptionsStandardPackagingCost:
          form.orderOptionsStandardPackagingCost,
        orderOptionsSpecificPackagingCost:
          form.orderOptionsSpecificPackagingCost,
      },
    });
  }

  function updateEmailPreferences(form: EmailPreferenceForm) {
    setIsDirty(true);
    setProfileConfigurations({
      ...profileConfigurations!,
      emailPreferences: {
        emailPreferenceNewDesign: form.emailPreferenceNewDesign,
        emailPreferenceNewLogos: form.emailPreferenceNewLogos,
        emailPreferenceNewOrder: form.emailPreferenceNewOrder,
        emailPreferencePendingOrder: form.emailPreferencePendingOrder,
        emailPreferenceOrder: form.emailPreferenceOrder,
      },
    });
  }

  function resetProfile() {
    setProfileConfigurations(profile);
    setPendingLogoState({ type: "none" });
    setCommissionProfile(mapCommissionProfile(organization));
    updateOrganizationData();
    setIsDirty(false);
    resetWhiteLabel();
  }

  async function saveProfileHandle() {
    setTransition(true);
    const toastId = useLoadingToast(`Saving`);

    try {
      const orgId = profile.userOrganizationInformations.organizationId;

      // Handle pending logo changes first
      if (pendingLogoState.type === "upload") {
        await organizationRepository.updateOrganizationLogo(
          orgId,
          pendingLogoState.file
        );
      } else if (pendingLogoState.type === "remove") {
        await organizationRepository.deleteOrganizationLogo(orgId);
      }

      // Update profile configurations
      await profileRepository.updateProfile(profileConfigurations!);

      // Update organization
      await organizationRepository.updateOrganization({
        ...organization,
        // The API now persists the contact fields for the vendor's own org
        // (Users & contact section's primary contact card).
        contactPerson: detailsForm?.contactPerson ?? "",
        contactPhone: detailsForm?.contactPhone ?? "",
        contactEmail: detailsForm?.contactEmail ?? "",
        shippingName: detailsForm?.shippingName ?? "",
        shippingAddress: detailsForm?.shippingAddress ?? "",
        shippingPostCode: detailsForm?.shippingPostCode ?? "",
        shippingCity: detailsForm?.shippingCity ?? "",
        shippingCountry: detailsForm?.shippingCountry ?? "",
        shippingContactPerson: detailsForm?.shippingContactPerson ?? "",
        shippingContactPhone: detailsForm?.shippingContactPhone ?? "",
        shippingContactEmail: detailsForm?.shippingContactEmail ?? "",
        defaultVatRate: detailsForm?.defaultVatRate ?? 0,
      });

      // Update commission profile
      await organizationRepository.setCommissionProfile(orgId, commissionProfile!);

      // Refetch profile and update state
      const refreshedProfile = await refetchProfile();
      if (refreshedProfile.data) {
        updateLogo(refreshedProfile.data);
      }

      // Clear pending logo state
      setPendingLogoState({ type: "none" });
      setIsDirty(false);

      useSuccessToast(t("toasts.profile_update_success"));
    } catch (error) {
      useErrorToast(t("toasts.profile_update_failed"));
    } finally {
      dissmissToast(toastId);
      setTransition(false);
    }
  }

  return {
    resetProfile: resetProfile,
    saveProfileChanges: saveProfileHandle,
    updateEmailPreferences: updateEmailPreferences,
    updateLogoHandle: updateOrganizationLogoHandle,
    removeLogoHandle: removeLogoHandle,
    cancelLogoRemovalHandle: cancelLogoRemovalHandle,
    updateOrganizationDetails: updateDetails,
    updateWhiteLabel: updateWhiteLabel,
    updateOrderOptions: updateOrderOptions,
    sections: sections,
    profile: profileConfigurations!,
    detailsForm: detailsForm,
    emailPreferenceForm: initialEmailPreferenceform,
    loading: loading,
    transition: transition,
    isDirty: isDirty,
    isVendor: true,
    vendorId: organization?.id,
    isAdmin: false,
    logoOptions: logoOptions,
    updateCommission: updateCommission,
    commissionProfile: commissionProfile,
    prefetchCommissionData: prefetchCommissionData,
    pendingLogoUploadPreview:
      pendingLogoState.type === "upload" ? pendingLogoState.previewUrl : undefined,
    pendingLogoRemoval: pendingLogoState.type === "remove",
  };
}
